import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { movies } from '@/mocks/movies';
import FilterBar, { type CategoryFilters } from './components/FilterBar';
import Pagination from './components/Pagination';

const PAGE_SIZE = 8;

const DEFAULT_FILTERS: CategoryFilters = { type: '全部', year: '全部', sort: '综合排序' };

export default function Category() {
  const [searchParams] = useSearchParams();
  const keyword = (searchParams.get('q') || '').trim();

  const [filters, setFilters] = useState<CategoryFilters>(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);

  const years = useMemo(
    () => Array.from(new Set(movies.map((m) => m.year))).sort((a, b) => b - a).map(String),
    []
  );

  const filtered = useMemo(() => {
    let list = movies.slice();

    if (keyword) {
      const k = keyword.toLowerCase();
      list = list.filter(
        (m) =>
          m.title.toLowerCase().includes(k) ||
          m.enTitle.toLowerCase().includes(k) ||
          m.director.toLowerCase().includes(k) ||
          m.genres.some((g) => g.toLowerCase().includes(k)) ||
          m.cast.some((c) => c.toLowerCase().includes(k))
      );
    }
    if (filters.type !== '全部') list = list.filter((m) => m.category === filters.type);
    if (filters.year !== '全部') list = list.filter((m) => String(m.year) === filters.year);

    switch (filters.sort) {
      case '评分从高到低':
        list.sort((a, b) => b.rating - a.rating);
        break;
      case '评分从低到高':
        list.sort((a, b) => a.rating - b.rating);
        break;
      case '最新更新':
        list.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
        break;
      default:
        list.sort((a, b) => Number(b.featured) - Number(a.featured) || b.rating - a.rating);
    }
    return list;
  }, [keyword, filters]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [keyword, filters]);

  const handleChange = (next: Partial<CategoryFilters>) =>
    setFilters((prev) => ({ ...prev, ...next }));
  const handleReset = () => setFilters(DEFAULT_FILTERS);

  return (
    <div className="min-h-screen flex flex-col bg-background-100">
      <Navbar />
      <div className="h-16 md:h-[72px]" />

      <main className="flex-1">
        <div className="w-full px-4 md:px-8 py-6 md:py-10">
          <Breadcrumb items={[{ label: '影视分类' }]} />

          <div className="mt-6">
            <h1 className="font-heading font-extrabold text-2xl md:text-4xl text-foreground-950">
              美国式禁忌4 及相关影视作品
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
              浏览与《美国式禁忌4》题材相近的电影与美剧作品，可按类型、年份筛选并按评分或更新时间排序，快速找到你想看的内容。
            </p>
          </div>

          {keyword && (
            <div className="mt-6 flex items-center flex-wrap gap-2 text-sm">
              <span className="text-foreground-500">搜索关键词：</span>
              <span className="px-3 py-1 rounded-md bg-accent-100 text-accent-900 font-medium">
                「{keyword}」
              </span>
              <span className="text-foreground-500">共 {filtered.length} 个结果</span>
            </div>
          )}

          <div className="mt-6">
            <FilterBar
              filters={filters}
              years={years}
              resultCount={filtered.length}
              onChange={handleChange}
              onReset={handleReset}
            />
          </div>

          {paged.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5 mt-8">
                {paged.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
              <Pagination current={page} total={totalPages} onChange={setPage} />
            </>
          ) : (
            <div className="mt-16 flex flex-col items-center justify-center text-center py-16">
              <span className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center text-foreground-400 mb-4">
                <i className="ri-search-eye-line text-3xl"></i>
              </span>
              <p className="font-heading font-semibold text-lg text-foreground-900">没有找到匹配的影视作品</p>
              <p className="text-sm text-foreground-500 mt-2 max-w-md">
                换个关键词试试，或者点击下方按钮重置筛选条件，浏览全部内容。
              </p>
              <button
                onClick={handleReset}
                className="mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line mr-1.5"></i> 重置筛选条件
              </button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}